import type {ReactElement} from 'react';
import {useContext} from 'react';
import type {Editor} from 'grapesjs';
import {EmailMakerAiRedesignPromptBox} from '@Components/email-maker-ai-redesign-prompt-box/email-maker-ai-redesign-prompt-box';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EditorViewport} from '@EmailMakerReact/email-maker-react.types';
import {openEmailMakerMobileControlModal} from '@Components/modals/email-maker-mobile-control-modal/email-maker-mobile-control-modal';
import {getResolvedTiles} from '@EmailMakerReact/lib/email-maker-tile-meta-library';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import {EmailMakerNav} from '@EmailMakerReact/layout/email-maker-nav/email-maker-nav';
import {EmailMakerCanvas} from '@EmailMakerReact/layout/email-maker-canvas/email-maker-canvas';
import {EmailMakerLeftPanel} from '@EmailMakerReact/layout/email-maker-left-panel/email-maker-left-panel';
import {EmailMakerRightPanel} from '@EmailMakerReact/layout/email-maker-right-panel/email-maker-right-panel';
import {EmailMakerBottomBar} from '@EmailMakerReact/layout/email-maker-bottom-bar/email-maker-bottom-bar';
import {useLoadingToastBottomOffset} from '@EmailMakerReact/hooks/use-loading-toast-bottom-offset';
import type {EmailMakerLayoutProps} from './email-maker-layout.types';
import styles from './email-maker-layout.module.scss';

export function EmailMakerLayout({onCanvasContainerReady}: EmailMakerLayoutProps): ReactElement {
  const {editor, state, store} = useContext(emailMakerContext);
  const isDesktop = state.editorViewport === EditorViewport.DESKTOP;

  useLoadingToastBottomOffset(state.editorViewport);

  function handleTileSelect(flatIndex: number): void {
    const selected = state.selectedComponent;
    if (!selected || !editor) {
      return;
    }
    const tiles = getResolvedTiles(editor, selected, store);
    const entry = tiles.find((t) => t.flatIndex === flatIndex);
    if (!entry) {
      return;
    }
    const isFullHeight = entry.meta.complexity === TileComplexity.COMPLEX;
    openEmailMakerMobileControlModal({flatIndex, isFullHeight});
  }

  return (
    <main className={styles.layout}>
      <EmailMakerNav />
      <div className={`${styles.body} ${isDesktop ? '' : styles.bodyMobile}`}>
        <div className={styles.editorControls}>
          <EmailMakerLeftPanel />
          <EmailMakerCanvas onContainerReady={onCanvasContainerReady} />
        </div>
        {renderDesktopRightPanel(isDesktop)}
      </div>
      {renderMobileBottomBar(isDesktop, handleTileSelect)}
      {renderMobileAiRedesignOpener(isDesktop, editor, store.isEmailEditorAiTextGenerationEnabled)}
    </main>
  );
}

/** Mounted at layout level so it survives block-sheet open/close; renders nothing until the mobile AI-Redesign tile requests the modal via redux. */
function renderMobileAiRedesignOpener(isDesktop: boolean, editor: Editor | null, isAiTextEnabled: boolean): ReactElement | null {
  if (isDesktop || !editor || !isAiTextEnabled) {
    return null;
  }
  return <EmailMakerAiRedesignPromptBox editor={editor} isModalOpenerOnly showInitialAnimation={false} />;
}

function renderDesktopRightPanel(isDesktop: boolean): ReactElement | null {
  if (!isDesktop) {
    return null;
  }
  return <EmailMakerRightPanel />;
}

function renderMobileBottomBar(isDesktop: boolean, onTileSelect: (flatIndex: number) => void): ReactElement | null {
  if (isDesktop) {
    return null;
  }
  return <EmailMakerBottomBar onTileSelect={onTileSelect} />;
}
